import { useEffect, useMemo, useState } from 'react';
import { api, type ComplianceCard } from '@/api/client';
import { mockCompliance, useMock } from '@/api/mock';

interface ArchiveItem {
  frameworkCode: string;
  generatedAt: string;
  generatedBy: string;
  sizeMb: number;
  status: 'Ready' | 'Generating' | 'Expired';
}

const archive: ArchiveItem[] = [
  { frameworkCode: 'SOC2', generatedAt: '2026-04-15', generatedBy: 'System', sizeMb: 12.8, status: 'Ready' },
  { frameworkCode: 'ISO27001', generatedAt: '2026-03-30', generatedBy: 'System', sizeMb: 15.1, status: 'Ready' },
  { frameworkCode: 'GDPR', generatedAt: '2026-05-01', generatedBy: 'Compliance Officer', sizeMb: 9.4, status: 'Ready' },
  { frameworkCode: 'HIPAA', generatedAt: '2026-05-18', generatedBy: 'System', sizeMb: 11.2, status: 'Generating' },
];

function formatDate(value?: string) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString();
}

export function ComplianceReportsPage() {
  const [cards, setCards] = useState<ComplianceCard[]>([]);

  useEffect(() => {
    if (useMock()) {
      setCards(mockCompliance);
      return;
    }
    api.complianceList().then(setCards).catch(() => setCards(mockCompliance));
  }, []);

  const stats = useMemo(() => ({
    frameworks: cards.length,
    openViolations: cards.reduce((sum, c) => sum + c.openViolations, 0),
  }), [cards]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Compliance Reports</h1>
        <p className="text-sm text-ink-muted mt-1">Report archive, control mapping, and download center.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card"><div className="card-title">Frameworks</div><div className="kpi-value">{stats.frameworks}</div></div>
        <div className="card"><div className="card-title">Open Violations</div><div className="kpi-value text-warn">{stats.openViolations}</div></div>
      </div>

      <div className="card overflow-auto">
        <div className="card-title mb-3">Framework Status</div>
        <table className="w-full text-sm min-w-[900px]">
          <thead className="text-ink-muted border-b border-border">
            <tr>
              <th className="text-left py-2">Framework</th>
              <th className="text-left py-2">Code</th>
              <th className="text-left py-2">Last Audit</th>
              <th className="text-left py-2">Status</th>
              <th className="text-left py-2">Open Violations</th>
              <th className="text-left py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {cards.map((c) => (
              <tr key={c.frameworkCode} className="border-b border-border/50 last:border-0">
                <td className="py-2">{c.frameworkName}</td>
                <td className="py-2">{c.frameworkCode}</td>
                <td className="py-2">{formatDate(c.lastGeneratedAt)}</td>
                <td className="py-2"><span className={c.status === 'Ready' ? 'badge-good' : 'badge-warn'}>{c.status}</span></td>
                <td className="py-2">{c.openViolations}</td>
                <td className="py-2">
                  <button className="text-accent-cyan hover:underline" onClick={() => api.generateReport(c.frameworkCode)}>
                    Generate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card overflow-auto">
        <div className="card-title mb-3">Historical Archive</div>
        <table className="w-full text-sm min-w-[820px]">
          <thead className="text-ink-muted border-b border-border">
            <tr>
              <th className="text-left py-2">Framework</th>
              <th className="text-left py-2">Generated At</th>
              <th className="text-left py-2">Generated By</th>
              <th className="text-left py-2">File Size</th>
              <th className="text-left py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {archive.map((a, idx) => (
              <tr key={`${a.frameworkCode}-${idx}`} className="border-b border-border/50 last:border-0">
                <td className="py-2">{a.frameworkCode}</td>
                <td className="py-2">{formatDate(a.generatedAt)}</td>
                <td className="py-2">{a.generatedBy}</td>
                <td className="py-2">{a.sizeMb} MB</td>
                <td className="py-2"><span className={a.status === 'Ready' ? 'badge-good' : a.status === 'Generating' ? 'badge-warn' : 'badge-info'}>{a.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
